import { createSelector } from 'reselect'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { getLuxPlusSubscriptionItems, isLuxPlusSubscriptionInCart } from 'checkout/selectors/view/luxPlusSubscription'
import { getTourV2Items } from '../view/toursv2'
import { isTourV2Offer } from 'lib/offer/offerTypes'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getTourItemKey } from 'lib/tours/tourUtils'
import { floatify } from 'lib/maths/mathUtils'
import getPayableTotal from './getPayableTotal'
import getDiscountTotal, { getVoucherDiscountTotal } from './getDiscountTotal'
import getPayableAfterModifiers from './getPayableAfterModifiers'
import type { PromoTypeLookup } from 'lib/promo/promoMappers'
import { filterItemDiscounts } from 'lib/promo/promoMappers'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'

export const checkoutPromotionView = createSelector(
  (state: App.State) => state.checkout.promo,
  (state: App.State) => getDiscountTotal(state),
  (state: App.State) => getVoucherDiscountTotal(state),
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => !!state.checkout.isFetchingPromo,
  (promotion, discount, voucherTotal, payableTotal, fetching): App.Checkout.PromotionView => {
    const cappedVoucherTotal = floatify(Math.min(voucherTotal, payableTotal))
    const remainingPayable = floatify(Math.max(payableTotal - cappedVoucherTotal, 0))
    const cappedDiscount = floatify(Math.min(discount, remainingPayable))
    return {
      promotion,
      discount: cappedDiscount,
      voucherTotal: cappedVoucherTotal,
      fetching,
    }
  },
)

export const checkoutCommissionView = createSelector(
  (state: App.State) => state.checkout.commission,
  (state: App.State) => state.checkout.commissionData,
  (state: App.State) => getDiscountTotal(state),
  (state: App.State) => getPayableTotal(state),
  (state: App.State) => !!state.checkout.isFetchingCommmission,
  (promotion, commissionData, discount, payableTotal, fetching): App.Checkout.PromotionView => {
    const cappedDiscount = discount > payableTotal ? payableTotal : discount

    return {
      promotion,
      discount: cappedDiscount,
      commissionPercentage: commissionData?.calculatedCommission ?? promotion?.discount,
      commissionDiscount: commissionData?.calculatedDiscount ?? promotion?.discountTotal ?? cappedDiscount,
      incentivePercentage: commissionData?.calculatedIncentive,
      incentiveDiscount: commissionData?.calculatedIncentiveAmount,
      totalCommissionPercentage: commissionData?.totalCalculatedCommission ?? promotion?.discount,
      totalCommissionDiscount: commissionData?.totalCalculatedDiscount ?? cappedDiscount,
      fetching,
    }
  },
)

export const getApplyCreditDisclaimer = createSelector(
  (state: App.State) => state.auth.account.creditsByCurrency[state.checkout.cart.currencyCode],
  (state: App.State) => getPayableAfterModifiers(state),
  (state: App.State) => isLuxPlusSubscriptionInCart(state),
  (state: App.State) => getLuxPlusSubscriptionItems(state),
  (credits, payable, isLuxPlusSubscriptionInCart, luxPlusCartSubscriptionItems): string | undefined => {
    const creditBalance = credits?.balance ?? 0

    if (isLuxPlusSubscriptionInCart) {
      const subscriptionItem = luxPlusCartSubscriptionItems[0]
      const maxCreditTotalPayableForSubscription = floatify(payable - subscriptionItem.amount)

      if (creditBalance > maxCreditTotalPayableForSubscription) {
        return getCurrentIntl().formatMessage(
          {
            id: 'checkout.luxPlus.subscriptionCardPaymentRequired',
            defaultMessage: 'Your {programName} annual subscription fee must be paid via card payment.',
            description: 'Disclaimer when LuxPlus subscription must be paid by card and credits exceed remaining balance',
          },
          { programName: LUXURY_PLUS.PROGRAM_NAME },
        )
      }
    }
  },
)

export const getSoldOutTourV2Offer = createSelector(
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => state.offer.offers,
  (state: App.State) => state.offer.tourV2Offers,
  (tourItems, offers, tourOffers): App.Tours.TourV2Offer | undefined => {
    let soldOutOffer
    tourItems.map(tourItem => {
      const tourItemKey = getTourItemKey(tourItem)
      const offer = (offers[tourItemKey] ?? tourOffers[tourItemKey]) as App.AnyOffer

      if (isTourV2Offer(offer)) {
        const departure = offer.departures[tourItem.purchasableOption.fkDepartureId]

        if (!departure) {
          soldOutOffer = offer
          return
        }

        const isUnavailable = departure.status === 'unavailable' || departure.status === 'unavailable-inactive-campaign'
        const datesNotMatching = departure.startDate !== tourItem.startDate || departure.endDate !== tourItem.endDate

        if (isUnavailable || datesNotMatching) {
          soldOutOffer = offers
        }
      }
    })

    return soldOutOffer
  },
)

export const isFetchingPromo = createSelector(
  (state: App.State) => state.checkout.isFetchingPromo ?? false,
  (isFetchingPromo) => isFetchingPromo,
)

export type GetPromoCodeItemDiscounts = {
  hasRequiredPromoCodeData: boolean
  codeName: string
  itemDiscounts: Array<App.Checkout.ItemDiscount>
}

/**
 * This is the preferred method for loading promo data as it is less error prone, extend this results of this selector with (carefully selected) promo information as required
 */
export const getPromoCodeItemDiscounts = createSelector(
  (state: App.State) => getPromoOrCommission(state),
  (state: App.State) => state.checkout.isFetchingPromo,
  (_: App.State, lookup?: PromoTypeLookup) => lookup?.specificType,
  (
    promo,
    isFetchingPromo,
    lookupSpecificType = 'all',
  ): GetPromoCodeItemDiscounts => {
    // (No promo applied)
    if (!promo) {
      return {
        hasRequiredPromoCodeData: true,
        codeName: '',
        itemDiscounts: [],
      }
    }

    if (isFetchingPromo) {
      return {
        hasRequiredPromoCodeData: false,
        codeName: '',
        itemDiscounts: [],
      }
    }

    const items = filterItemDiscounts({
      itemDiscounts: promo?.items,
      lookup: {
        specificType: lookupSpecificType as any,
      },
    })

    return {
      hasRequiredPromoCodeData: true,
      codeName: promo.code,
      itemDiscounts: items,
    }
  },
)

export const getAllPromoCodeItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'all' }),
)

export const getCruiseItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'cruise' }),
)

export const getTourAndCruiseItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'tourAndCruiseItems' }),
)

export const getSubReoccuringItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'subscription_reoccurring-fee' }),
)

export const getSubscriptionJoinDiscountItems = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'subscription_joining-fee' }),
)

export const getTransferItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'transfer' }),
)

export const getExperienceItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'experience' }),
)

export const getAccommodationAndExperienceItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'accommodationAndExperiences' }),
)

export const getInsuranceItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'insurance' }),
)

export const getMerchandiseItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'merchandise' }),
)

export const getGiftCardItemDiscounts = createSelector(
  (state: App.State) => state,
  (state: App.State) => getPromoCodeItemDiscounts(state, { specificType: 'gift_card' }),
)

type filterBreakdownItemDiscountsProps = {
  allItemDiscounts: Array<App.Checkout.ItemDiscount> | undefined
  iIds: Array<{
    offerId?: string
    itemId?: string
  }>
}

export function filterBreakdownItemDiscounts({ allItemDiscounts, iIds }: filterBreakdownItemDiscountsProps): Array<App.Checkout.ItemDiscount> {
  if (allItemDiscounts == undefined || allItemDiscounts?.length == 0 || iIds.length === 0) {
    return []
  }

  const res = allItemDiscounts.filter(id =>
    iIds.some(({ offerId, itemId }) => {
      if (id.categoryBK == 'experiences') {
        return offerId == id.offerId
      }

      return (offerId === id.offerId && itemId === id.itemId) || (itemId == id.itemId && !offerId) || (offerId == id.offerId && !itemId)
    }),
  )

  return res
}

const getPromoOrCommission = createSelector(
  (state: App.State) => state.checkout.promo,
  (state: App.State) => state.checkout.commission,
  (promo, commission) => isAgentHubEnabled ? commission : promo,
)

export const isPostPurchaseCustomOffer = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (existingOrder): boolean => {
    const purchaseType = existingOrder?.customOfferItems?.[0]?.custom_offer?.purchase_type
    return purchaseType === 'post-purchase'
  },
)

export const isCustomOfferCheckout = createSelector(
  (state: App.State) => state.checkout.cart.mode,
  (mode): boolean => mode === 'custom-offer' || mode === 'custom-offer-v2',
)
